import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ClarityModule, ClrLoadingState } from '@clr/angular';
import dayjs from 'dayjs';
import { ToastService } from 'src/app/services/toast.service';
import { firstValueFrom } from 'rxjs';
import { Row } from 'src/app/models/ui/table';
import { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { APIService } from 'src/app/services/APIService/api.service';
import { AuthService } from 'src/app/services/Auth/auth.service';
import { OverlayService } from 'src/app/services/Overlay/overlay.service';
import { SpinnerService } from 'src/app/services/Spinner/spinner.service';
import { CustomValidators } from 'src/app/shared/classes/custom-validators';
import { NewAuftragModel } from 'src/app/types/api';

@Component({
  selector: 'app-multi-auftrag-modal',
  templateUrl: './multi-auftrag-modal.component.html',
  styleUrls: ['./multi-auftrag-modal.component.scss'],
  imports: [ClarityModule, FormsModule, ReactiveFormsModule, MrTranslatePipe, BackendLocaleDatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiAuftragModalComponent {
  private readonly apiService = inject(APIService);
  private readonly spinner = inject(SpinnerService);
  private readonly overlayService = inject(OverlayService);
  private readonly toastr = inject(ToastService);
  private readonly authService = inject(AuthService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  protected readonly localeDate = inject(BackendLocaleDatePipe);

  readonly maengel = input<Row[]>([]);

  /** @emits true when saved, for reload */
  readonly onClose = output<boolean>();

  protected readonly isModalOpen = signal<boolean>(undefined);

  protected readonly loadState = signal<ClrLoadingState>(ClrLoadingState.DEFAULT);

  protected readonly selectedValid = computed(() => this.maengel().filter(r => isValid(r)));
  protected readonly selectedInvalid = computed(() =>
    this.maengel()
      .filter(r => !isValid(r))
      .map(r => r.anlagenobjekte)
  );
  private toReload = false;

  protected readonly trial: boolean;

  private readonly firmenResource = rxResource({
    params: () => this.isModalOpen() || undefined,
    stream: () => this.apiService.getFirmen(),
  });
  protected readonly firmen = computed(
    () =>
      this.firmenResource.value()?.rows.map(firma => ({
        ID: firma.ID,
        mFirma: `${firma.Firma}, ${firma.Abteilung}, ${firma.Ort}`,
      })) ?? []
  );

  private readonly werteResource = rxResource({
    params: () => this.isModalOpen() || undefined,
    stream: () => this.apiService.getNeuAuftraegWerte(),
  });

  protected readonly auftragsart = computed(() => this.werteResource.value()?.auftragart ?? []);

  private readonly gruppenResource = rxResource({
    params: () => this.isModalOpen() || undefined,
    stream: () => this.apiService.getAuftragsGroups(),
  });
  protected readonly gruppen = this.gruppenResource.value;

  protected readonly multiauftrag = new FormGroup({
    startdate: new FormControl('', Validators.required),
    enddate: new FormControl('', Validators.required),
    auftragnehmer: new FormControl<number>(null, Validators.required),
    auftragsgruppe: new FormControl(1, Validators.required),
    auftragsart: new FormControl(''),
    auftragsnr: new FormControl(''),
    // kostenart: new FormControl(''),
    bemerkung: new FormControl(''),
  });

  constructor() {
    effect(() => {
      if (this.isModalOpen() === false) this.onClose.emit(this.toReload);
    });
    const onceEffect = effect(() => {
      if (this.selectedValid().length) {
        this.isModalOpen.set(true);
        if (this.selectedInvalid().length) {
          this.toastr.warning(
            this.selectedInvalid().join('<br/>'),
            `${this.selectedInvalid().length} ${this.mrTranslate.transform('Mängel: Beauftragt, Erledigt oder Schreibgeschützt')}`,
            { enableHtml: true }
          );
        }
      } else {
        this.toastr.error(
          this.mrTranslate.transform(
            'Mängel im Status „Beauftragt“ oder „Erledigt“ oder ohne Schreibrechte können nicht geändert werden'
          ),
          this.mrTranslate.transform('Keine gültigen Mängel ausgewählt')
        );
        this.isModalOpen.set(false);
      }
      onceEffect.destroy();
    });

    const token = this.authService.getToken();
    this.trial = token.trial;

    const laenge = token.auftragslaenge || +this.authService.decrypt(localStorage.getItem('auftraglaenge')) || 3;
    this.multiauftrag.get('startdate').setValue(BackendLocaleDatePipe.today);
    this.multiauftrag
      .get('enddate')
      .setValue(dayjs().startOf('day').add(laenge, 'days').format(BackendLocaleDatePipe.format));

    this.multiauftrag.setValidators(CustomValidators.StartEndDatesValidator());
  }

  protected async sendMultiAuftrag() {
    this.spinner.enable();

    try {
      this.loadState.set(ClrLoadingState.LOADING);

      const objMangel: { [ostammid: number]: number[] } = {};
      for (const mngl of this.selectedValid()) {
        objMangel[+mngl.ostammid] ??= [];
        objMangel[+mngl.ostammid].push(+mngl.omazsid as any);
      }

      let sendObj: NewAuftragModel = {
        ...this.multiauftrag.getRawValue(),
        titel: '',
        kostenart: '',
        omazsids: objMangel,
        language: localStorage.getItem('language'),
      };

      const data = await firstValueFrom(this.apiService.sendAuftrag(sendObj));

      if (data) {
        this.toastr.success(this.mrTranslate.transform('Eintrag erfolgreich gespeichert'));
        this.loadState.set(ClrLoadingState.SUCCESS);
        this.toReload = true;
        this.isModalOpen.set(false);

        this.overlayService.setOverlay({
          overlay: 'viewAuftrag',
          data,
          auftragsnr: sendObj.auftragsnr,
          zIndex: 5,
        });
      } else throw new Error();
    } catch (_) {
      this.loadState.set(ClrLoadingState.DEFAULT);
      this.toastr.error(this.mrTranslate.transform('Ein Fehler ist aufgetreten'));
      console.log(_);
    } finally {
      this.spinner.disable();
    }
  }
}
function isValid(x: Row): boolean {
  return !x.erledigt && !x.auftrag && x.rechte == 2;
}
